import React from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import DOMPurify from "dompurify";
import api from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";

export default function BlogPost() {
  const { slug } = useParams();
  const { data: post, isLoading, isError } = useQuery({
    queryKey: ["blog", slug],
    queryFn: async () => (await api.get(`/blog/${slug}`)).data,
  });

  if (isLoading) {
    return (
      <div className="mx-auto max-w-3xl px-5 py-16 sm:px-8">
        <Skeleton className="h-6 w-32" />
        <Skeleton className="mt-6 h-12 w-full" />
        <Skeleton className="mt-8 h-72 w-full rounded-xl" />
        <div className="mt-8 space-y-3"><Skeleton className="h-4 w-full" /><Skeleton className="h-4 w-full" /><Skeleton className="h-4 w-2/3" /></div>
      </div>
    );
  }

  if (isError || !post) {
    return (
      <div className="mx-auto max-w-3xl px-5 py-24 text-center">
        <h1 className="text-2xl font-bold text-pulse-navy">Post not found</h1>
        <Link to="/blog" className="mt-4 inline-block text-pulse-green">Back to the journal</Link>
      </div>
    );
  }

  return (
    <article className="mx-auto max-w-3xl px-5 py-16 sm:px-8 sm:py-20" data-testid="blog-post">
      <Link to="/blog" className="flex w-fit items-center gap-2 text-sm font-medium text-slate transition-colors hover:text-pulse-navy" data-testid="blog-back-link">
        <ArrowLeft size={16} /> The Pulse Journal
      </Link>
      <div className="mt-8 text-xs font-medium uppercase tracking-wider text-pulse-green">{post.read_time}</div>
      <h1 className="mt-3 text-4xl font-bold leading-tight tracking-tight text-pulse-navy md:text-5xl">{post.title}</h1>
      <p className="mt-5 text-lg text-slate">{post.excerpt}</p>
      <div className="mt-10 overflow-hidden rounded-xl">
        <img src={post.cover} alt={post.title} className="h-full w-full object-cover" />
      </div>
      <div
        className="prose-pulse mt-10 space-y-5 text-base leading-relaxed text-charcoal [&_h2]:mt-8 [&_h2]:text-2xl [&_h2]:font-semibold [&_h2]:text-pulse-navy [&_b]:font-semibold [&_p]:text-charcoal"
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.content || "") }}
      />
      <div className="mt-14 rounded-xl bg-cloud p-8 text-center">
        <h3 className="text-2xl font-bold text-pulse-navy">Bring calm to your bookings.</h3>
        <p className="mt-2 text-slate">Start your 15-day free trial of Pulse.</p>
        <Link to="/pricing" className="mt-5 inline-block rounded-md bg-pulse-green px-6 py-3 text-sm font-semibold text-white transition-transform hover:scale-[1.02]">
          Start free trial
        </Link>
      </div>
    </article>
  );
}
